// Zugsystem: baut die Kompositionen, stellt Decks/Griffe/Malflächen bereit und
// bewegt alle Züge auf dem Fahrplan. Der Spieler sitzt im Waggon-Bezugssystem
// des *lokalen* Zuges — deshalb müssen matrixWorld und ctx.train.jolt hier
// pro Frame stimmen, bevor player/camera laufen (Konzept §12.1, CONTRACT.md).
//
// Zwei Züge (Konzept §10.1 / §16 Frage 3): LOCAL ist hoppbar und bleibt
// ctx.train; THROUGH ist nur Beobachtungsgegenstand (boardable: false) und
// steht zusätzlich in ctx.trains. Identische API-Felder pro Eintrag.

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import {
  buildConsist, CAR_TYPES, CONSIST_PLAN, THROUGH_CONSIST_PLAN,
} from '../../shared/consist.js';
import {
  trackPoint, trackCant, trackRight, railTopY,
  BRANCH_MAIN, BRANCH_MINE, JUNCTION_MAIN_S, MINE_TRACK_LENGTH,
  STATIONS,
} from '../../shared/track.js';
import {
  allTrainsAt, trainStateAt, realSpeedFrom, trainWorldPos,
  TRAINS, TRAIN_LOCAL, TRAIN_THROUGH, TRAIN_SIGHT_M,
} from '../../shared/schedule.js';
import { mat, matShiny, box, cylinder, plane, PALETTE } from '../render/style.js';

// Cull-Distanz: Fog far ≈ 800 — Silhouette im Nebel noch sichtbar, darüber
// spart der zweite Zug Draw Calls (fast Verdopplung der Zugkosten).
const TRAIN_CULL_M = Math.max(TRAIN_SIGHT_M * 2.2, 900);

// Süd-Nebengleis: THROUGH steht parallel zum Hauptgleis (STATIONS[0].sidings).
const SOUTH_SIDING_LATERAL = STATIONS[0]?.sidings?.[0]?.lateral ?? -9.0;

// --- Wiederverwendete Scratch-Objekte (kein Frame-Allok) --------------------
const _pC = { x: 0, y: 0, z: 0 };
const _pF = { x: 0, y: 0, z: 0 };
const _pB = { x: 0, y: 0, z: 0 };
const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();
const _up = new THREE.Vector3();
const _pos = new THREE.Vector3();
const _local = new THREE.Vector3();
const _world = new THREE.Vector3();
const _inv = new THREE.Matrix4();
const _basis = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _scaleOne = new THREE.Vector3(1, 1, 1);
const _idColor = new THREE.Color();
const _rustColor = new THREE.Color();
// Lateral-Offset (Nebengleis) — plain object, trackRight schreibt hinein.
const _latRight = { x: 0, y: 0, z: 0 };

// Material-Caches jenseits von style.js (identitätsabhängig, aber endlich viele)
const bodyMatCache = new Map();
const stencilMatCache = new Map();

/** Asset-Bibliothek für den Waggonbau — null = reiner Code-Pfad. */
let _assets = null;

// Waggontypen, die als glTF geladen werden (plus wiederverwendbares Drehgestell).
const CAR_MODEL_NAMES = ['loco', 'hopper', 'boxcar', 'tank', 'gondola', 'flat', 'bogie'];

// ---------------------------------------------------------------------------

export class TrainSystem {
  async init(ctx) {
    this.THREE = ctx.THREE || THREE;
    this._units = [];
    this._listenerScratch = { x: 0, y: 0, z: 0 };
    this._headPosScratch = { x: 0, y: 0, z: 0 };

    // Modelle optional laden. Fehlt etwas, bleibt _assets nutzbar und
    // instance() liefert null — dann greift die Code-Geometrie.
    _assets = ctx.assets ?? null;
    if (_assets?.loadMany) {
      const requests = CAR_MODEL_NAMES.map((name) => {
        const t = CAR_TYPES[name];
        return t
          ? { name, expectedSize: { length: t.length, width: t.width, height: t.height } }
          : { name };
      });
      const n = await _assets.loadMany(requests);
      // console.log, nicht ctx.log: HUD ist zu diesem Zeitpunkt noch nicht init.
      console.log(`[train] Assets: ${n}/${CAR_MODEL_NAMES.length} geladen`);
    }

    // Pro TRAINS-Eintrag eigene Komposition + Wurzel-Group.
    for (const meta of TRAINS) {
      const plan = planForTrain(meta);
      const unit = this._buildUnit(meta, plan);
      this._units.push(unit);
      ctx.scene.add(unit.root);
    }

    // Abwärtskompatibel: ctx.train = LOCAL (Spieler, Aufspringen, Kälte).
    this._local = this._units.find((u) => u.id === TRAIN_LOCAL) ?? this._units[0];
    this.root = this._local.root;
    this.cars = this._local.cars;
    this._byId = this._local._byId;
    this.totalLength = this._local.totalLength;
    this._api = this._local.api;
    this._prevSpeed = 0;
    this._prevWorldSeconds = 0;
    this._jolt = 0;
    this._railPhase = 0;

    ctx.train = this._local.api;
    ctx.trains = this._units.map((u) => u.api);

    // Auch vor der ersten Serverantwort (worldSeconds === 0) gültig platzieren.
    this._applyAll(ctx, ctx.worldSeconds ?? 0, 0);
  }

  update(dt, ctx) {
    const ws = ctx.worldSeconds ?? 0;
    this._applyAll(ctx, ws, dt);
    ctx.train = this._local.api;
    ctx.trains = this._units.map((u) => u.api);
  }

  dispose() {
    for (const unit of this._units ?? []) {
      if (unit.root?.parent) unit.root.parent.remove(unit.root);
      for (const car of unit.cars) {
        for (const s of car.paintSurfaces) {
          s.material?.map?.dispose?.();
          s.material?.dispose?.();
        }
      }
    }
  }

  localToWorld(carId, localVec3, outVec3 = new THREE.Vector3()) {
    const car = this._findCar(carId);
    if (!car) return outVec3.set(0, 0, 0);
    _local.copy(localVec3);
    return outVec3.copy(_local).applyMatrix4(car.group.matrixWorld);
  }

  worldToLocal(carId, worldVec3, outVec3 = new THREE.Vector3()) {
    const car = this._findCar(carId);
    if (!car) return outVec3.set(0, 0, 0);
    _inv.copy(car.group.matrixWorld).invert();
    _world.copy(worldVec3);
    return outVec3.copy(_world).applyMatrix4(_inv);
  }

  /**
   * Waggon-Suche nur auf dem lokalen (hoppbaren) Zug — Aufspringen darf den
   * durchfahrenden Güterzug nicht als Kletterziel anbieten.
   */
  carAt(worldVec3, maxDist = 2.5) {
    let best = null;
    let bestD = maxDist;
    for (const car of this._local.cars) {
      this.worldToLocal(car.id, worldVec3, _local);

      // `maxDist` ist die Suchweite *außerhalb* des Waggons. Der alte Filter
      // erlaubte unabhängig davon nur 0,5 m neben der Hülle. Damit wurde ein
      // Spieler vor einer sichtbaren Leiter verworfen, bevor die eigentliche
      // Distanzprüfung überhaupt lief. Der breite Vorfilter spart weiter die
      // Deck-Prüfung für ferne Wagen, bildet jetzt aber die API-Semantik ab.
      const shellHalfL = car.length * 0.5;
      const shellHalfW = car.type.width * 0.5;
      if (_local.z < -shellHalfL - maxDist || _local.z > shellHalfL + maxDist) continue;
      if (_local.x < -shellHalfW - maxDist || _local.x > shellHalfW + maxDist) continue;
      if (_local.y < -maxDist || _local.y > car.type.height + maxDist) continue;

      // Abstand zur Außenhülle, nicht zum Deck: Eine Leiter beginnt unten am
      // Rahmen; beim hohen Hopper wäre der Spieler dort mehrere Meter vom
      // Kohle-/Randdeck entfernt, obwohl seine Hand direkt vor der Sprosse ist.
      // Die nachgelagerte Leiter- bzw. Mantle-Suche entscheidet anschließend,
      // ob an dieser Stelle wirklich etwas Greifbares existiert.
      const cx = THREE.MathUtils.clamp(_local.x, -shellHalfW, shellHalfW);
      const cy = THREE.MathUtils.clamp(_local.y, 0, car.type.height);
      const cz = THREE.MathUtils.clamp(_local.z, -shellHalfL, shellHalfL);
      const d = Math.hypot(_local.x - cx, _local.y - cy, _local.z - cz);
      if (d <= bestD) {
        bestD = d;
        best = car;
      }
    }
    return best;
  }

  _findCar(carId) {
    for (const unit of this._units) {
      const c = unit._byId.get(carId);
      if (c) return c;
    }
    return null;
  }

  _buildUnit(meta, plan) {
    const consist = buildConsist(plan);
    const root = new THREE.Group();
    root.name = `train-${meta.id}`;
    const cars = [];
    const byId = new Map();
    for (const raw of consist.cars) {
      const car = buildCarInstance(raw);
      root.add(car.group);
      cars.push(car);
      byId.set(car.id, car);
    }

    const unit = {
      id: meta.id,
      role: meta.role,
      boardable: !!meta.boardable,
      root,
      cars,
      _byId: byId,
      totalLength: consist.totalLength,
      _prevSpeed: 0,
      _jolt: 0,
      _railPhase: 0,
      api: null,
    };

    // API-Objekt einmal bauen; Felder werden in update() überschrieben.
    // localToWorld/worldToLocal/carAt: boardable-Züge nutzen die System-API
    // (Spieler); non-boardable liefern null bei carAt.
    unit.api = {
      id: meta.id,
      role: meta.role,
      boardable: !!meta.boardable,
      headS: 0,
      speed: 0,
      realSpeed: 0,
      direction: 0,
      state: 'standing',
      station: meta.id === TRAIN_LOCAL ? 'south' : null,
      branch: BRANCH_MAIN,
      runKind: meta.id === TRAIN_LOCAL ? 'main' : 'through',
      cars,
      jolt: 0,
      carById: (id) => byId.get(id) ?? null,
      localToWorld: (carId, localVec3, outVec3) => this.localToWorld(carId, localVec3, outVec3),
      worldToLocal: (carId, worldVec3, outVec3) => this.worldToLocal(carId, worldVec3, outVec3),
      carAt: (worldVec3, maxDist = 2.5) => (
        unit.boardable ? this.carAt(worldVec3, maxDist) : null
      ),
    };
    return unit;
  }

  // --- Bewegung -------------------------------------------------------------

  _applyAll(ctx, worldSeconds, dt) {
    const states = allTrainsAt(worldSeconds);
    const byId = new Map();
    for (const st of states) byId.set(st.id, st);

    // Hörer/Kamera für Cull — Spielerposition bevorzugt.
    const listener = this._listenerPos(ctx);

    for (const unit of this._units) {
      // Fallback: trainStateAt nur für LOCAL, falls allTrainsAt fehlt.
      const st = byId.get(unit.id)
        ?? (unit.id === TRAIN_LOCAL ? trainStateAt(worldSeconds) : null);
      if (!st) continue;
      this._applyUnitState(unit, st, worldSeconds, dt);

      // LOCAL nie cullen (Spieler kann mitfahren, auch wenn „fern").
      // THROUGH: unsichtbar und damit draw-call-frei jenseits der Cull-Distanz.
      if (unit.id !== TRAIN_LOCAL) {
        trainWorldPos(st, this._headPosScratch);
        const dx = this._headPosScratch.x - listener.x;
        const dy = this._headPosScratch.y - listener.y;
        const dz = this._headPosScratch.z - listener.z;
        const dist = Math.hypot(dx, dy, dz);
        unit.root.visible = dist <= TRAIN_CULL_M;
      } else {
        unit.root.visible = true;
      }
    }

    // Spiegeln der LOCAL-Felder auf System-Aliases (Tests/ältere Leser).
    this._prevSpeed = this._local._prevSpeed;
    this._jolt = this._local._jolt;
    this._railPhase = this._local._railPhase;
    this._prevWorldSeconds = worldSeconds;
  }

  _listenerPos(ctx) {
    const out = this._listenerScratch;
    const ps = ctx.playerState;
    if (ps?.pos && Number.isFinite(ps.pos.x)) {
      out.x = ps.pos.x;
      out.y = ps.pos.y;
      out.z = ps.pos.z;
      return out;
    }
    if (ctx.playerPos && Number.isFinite(ctx.playerPos.x)) {
      out.x = ctx.playerPos.x;
      out.y = ctx.playerPos.y;
      out.z = ctx.playerPos.z;
      return out;
    }
    const cam = ctx.camera;
    if (cam?.position) {
      out.x = cam.position.x;
      out.y = cam.position.y;
      out.z = cam.position.z;
      return out;
    }
    // Spawn-Nähe Südhof.
    trackPoint(260, out, BRANCH_MAIN);
    out.y += 1.6;
    return out;
  }

  _applyUnitState(unit, st, worldSeconds, dt) {
    const realSpeed = realSpeedFrom(st.speed);
    // Strecke der Zugspitze — alle Waggons folgen dieser Branch-ID (s. unten).
    const headBranch = st.branch ?? BRANCH_MAIN;

    // Beschleunigung aus Geschwindigkeitsänderung (Weltzeit → Echtzeit-Näherung).
    let accel = 0;
    if (dt > 1e-4) {
      accel = (st.speed - unit._prevSpeed) / Math.max(dt, 1e-3);
    }
    unit._prevSpeed = st.speed;

    // Kupplungsspiel: stärker beim Anfahren/Bremsen, sonst leises Rattern.
    const accelMag = Math.min(1, Math.abs(accel) * 8);
    const moving = st.state === 'moving' ? 1 : 0;
    // Schienenstöße: Phase läuft mit zurückgelegter Strecke (Echtzeit-m).
    unit._railPhase += realSpeed * Math.max(dt, 0);
    const joint = moving * Math.max(0, Math.sin(unit._railPhase * (Math.PI * 2 / 18.5)));
    const jointKick = joint * joint; // scharfe Spitzen alle ~Schwellenpakete
    unit._jolt = Math.min(1, moving * (0.12 + accelMag * 0.55 + jointKick * 0.45));

    // Nebengleis-Lateral: THROUGH am Südhof parallel zum Hauptgleis, damit
    // LOCAL vorbeifahren kann ohne geometrische Durchdringung.
    const lateral = lateralForTrain(unit, st);

    for (const car of unit.cars) {
      // Deterministischer Längsversatz: waggonabhängige Phase, wenige cm.
      const phase = car.index * 1.73 + car.offset * 0.05;
      const coupleAmp = 0.012 + accelMag * 0.045 + moving * 0.008;
      const couple = Math.sin(worldSeconds * 2.7 + phase) * coupleAmp
        + Math.sin(worldSeconds * 5.1 + phase * 1.3) * coupleAmp * 0.35;

      // headS − offset: Lok immer am s-hohen Ende; Rückfahrt = geschoben.
      car.s = st.headS - car.offset + couple;
      car.branch = headBranch;
      this._placeCar(car, car.s, couple, headBranch, lateral);
    }

    // matrixWorld muss im selben Frame für den Spieler gelten.
    unit.root.updateMatrixWorld(true);

    const api = unit.api;
    api.headS = st.headS;
    api.speed = st.speed;
    api.realSpeed = realSpeed;
    api.direction = st.direction;
    api.state = st.state;
    api.station = st.station;
    api.branch = headBranch;
    api.runKind = st.runKind ?? (unit.id === TRAIN_THROUGH ? 'through' : 'main');
    api.jolt = unit._jolt;
  }

  /**
   * Richtet den Waggon zwischen seinen Drehgestellen aus — Pitch aus der
   * Höhendifferenz vorne/hinten, Roll aus Überhöhung. Ohne das schwebt der
   * Zug über Kurven und Rampen.
   *
   * Branch: alle Waggons nutzen die Strecke der Zugspitze (trainStateAt.branch).
   * Begründung: Der Fahrplan wechselt die Branch nur an der Weiche in zwei
   * Legs mit identischer Weltposition (main@JUNCTION ≡ mine@0). Eine echte
   * Split-Geometrie pro Waggon (180 m Zug auf beiden Strecken) wäre teurer und
   * fehleranfälliger; die ehrliche Näherung ist „Spitze bestimmt die Strecke“.
   * Ragt ein Waggon mit s < 0 über die Mine-Weiche hinaus, liegt er geometrisch
   * auf der Hauptstrecke vor der Weiche (JUNCTION + s) — so bleiben Abstände
   * und Weltstetigkeit erhalten, ohne pro Achse zwei Branches zu mischen.
   *
   * lateral: Versatz auf trackRight (Nebengleis). 0 = Hauptgleis.
   */
  _placeCar(car, s, coupleZ, branch = BRANCH_MAIN, lateral = 0) {
    const half = car.length * 0.5;
    // Drei Sample-Punkte (Mitte/vorne/hinten) — jeder kann an der Weiche
    // auf die gekoppelte Strecke fallen.
    placeOnNetwork(s, branch, _pC, _scratchPlace);
    const yC = _scratchPlace.yRail;
    const bC = _scratchPlace.branch;
    const sC = _scratchPlace.s;

    placeOnNetwork(s + half, branch, _pF, _scratchPlace);
    const yF = _scratchPlace.yRail;

    placeOnNetwork(s - half, branch, _pB, _scratchPlace);
    const yB = _scratchPlace.yRail;

    // Vorwärts = Richtung steigendes s (Lok-Ende). Geometrie wird nie umgedreht.
    _fwd.set(_pF.x - _pB.x, yF - yB, _pF.z - _pB.z);
    const flen = _fwd.length() || 1;
    _fwd.multiplyScalar(1 / flen);

    // Rechts in der XZ-Ebene (trackRight-Konvention), dann mit Cant verkippen.
    _right.set(_fwd.z, 0, -_fwd.x);
    const rlen = Math.hypot(_right.x, _right.z) || 1;
    _right.x /= rlen;
    _right.z /= rlen;
    _right.y = 0;

    const cant = trackCant(sC, bC);
    // Rechte Schiene höher bei cant>0 → Wagen rollt nach links.
    _up.set(-_right.x * cant, 1, -_right.z * cant).normalize();
    // Orthonormalisieren: Y = Z×X, X = Y×Z
    _right.crossVectors(_up, _fwd).normalize();
    _up.crossVectors(_fwd, _right).normalize();

    // Kupplungsspiel auch optisch längs (lokal −Z, weil +Z zur Spitze zeigt und
    // couple schon in s steckt — hier nur Feinschliff unter der Wahrnehmbarkeit).
    _pos.set(
      _pC.x + _fwd.x * coupleZ * 0.15,
      yC,
      _pC.z + _fwd.z * coupleZ * 0.15,
    );

    // Nebengleis: seitlicher Versatz in Gleis-Rechts-Richtung (ohne Cant).
    if (lateral !== 0) {
      trackRight(sC, _latRight, bC);
      _pos.x += _latRight.x * lateral;
      _pos.z += _latRight.z * lateral;
    }

    _basis.makeBasis(_right, _up, _fwd);
    _q.setFromRotationMatrix(_basis);

    const g = car.group;
    g.position.copy(_pos);
    g.quaternion.copy(_q);
    g.scale.copy(_scaleOne);
    g.updateMatrix();
  }
}

/** Plan zur Zug-Metadaten-Hint-Zuordnung. */
function planForTrain(meta) {
  if (meta.id === TRAIN_THROUGH || meta.consistHint === 'through-freight') {
    return THROUGH_CONSIST_PLAN;
  }
  return CONSIST_PLAN;
}

/**
 * Lateral-Offset: THROUGH am Südhof auf dem West-Nebengleis.
 * Nur stehend — bei Fahrt liegt er auf dem Hauptgleis (Korridor).
 */
function lateralForTrain(unit, st) {
  if (unit.id !== TRAIN_THROUGH) return 0;
  if (st.state === 'standing' && st.station === 'south') return SOUTH_SIDING_LATERAL;
  return 0;
}

// Scratch für placeOnNetwork — kein Frame-Allok.
const _scratchPlace = { s: 0, branch: BRANCH_MAIN, yRail: 0 };

/**
 * Mappt (s, branch) auf eine befahrbare Streckenposition.
 * Mine s < 0 → Hauptstrecke südlich der Weiche (gekoppelte Polylinie).
 * Schreibt Weltpunkt nach outPos, Metadaten nach outMeta.
 */
function placeOnNetwork(s, branch, outPos, outMeta) {
  let b = branch;
  let ss = s;
  if (b === BRANCH_MINE) {
    if (ss < 0) {
      // Hinter der Weiche Richtung Süd — gleiche Weltlinie wie Anfahrt.
      b = BRANCH_MAIN;
      ss = JUNCTION_MAIN_S + ss;
    } else if (ss > MINE_TRACK_LENGTH) {
      ss = MINE_TRACK_LENGTH;
    }
  }
  trackPoint(ss, outPos, b);
  outMeta.s = ss;
  outMeta.branch = b;
  outMeta.yRail = railTopY(ss, 0, b);
  return outPos;
}

// ===========================================================================
// Waggonbau
// ===========================================================================

function buildCarInstance(raw) {
  const car = {
    id: raw.id,
    index: raw.index,
    typeId: raw.typeId,
    type: raw.type,
    identity: raw.identity,
    offset: raw.offset,
    length: raw.length,
    s: 0,
    doorOpen: raw.doorOpen,
    loadLevel: raw.loadLevel,
    paintSurfaces: raw.paintSurfaces,
    decks: [],
    grabs: [],
    // Vertikale Leiter-Zonen (Linie, kein Punkt) — Quelle für Klettern.
    ladders: [],
    group: new THREE.Group(),
    usesModel: false,
    _doorParts: null,
  };
  car.group.name = car.id;
  // matrixWorld wird in _placeCar / updateMatrixWorld gesetzt; Auto-Update
  // am Root reicht, Kinder bleiben lokal.
  car.group.matrixAutoUpdate = true;

  // Modell ist reine Optik. Decks/Griffe/Kollision kommen immer aus dem Code.
  const modelInst = _assets?.instance?.(car.typeId) ?? null;
  if (modelInst) {
    car.usesModel = true;
    car.group.add(modelInst.root);
    applyModelIdentity(car, modelInst.root);
    // Decks + Griffe ohne Code-Meshes.
    setupCarDecksAndGrabs(car);
    // Kohleladung: Modelle bringen keine mit — Code-Ladung ins Modell.
    if (car.typeId === 'hopper') addHopperCoal(car);
    // Tür aus part_door verschieben.
    applyModelDoor(car, modelInst);
    // Malflächen aus paint_* — fehlen welche, fallen wir pro Fläche zurück.
    attachPaintSurfaces(car, modelInst);
  } else {
    const body = makeBodyMaterial(car.identity);
    const builders = {
      loco: buildLoco,
      hopper: buildHopper,
      boxcar: buildBoxcar,
      tank: buildTank,
      gondola: buildGondola,
      flat: buildFlat,
    };
    const build = builders[car.typeId] || buildFlat;
    build(car, body);
    applyDamageDetails(car);
    // Vor Malflächen mergen: sonst fallen surfaceId-Meshes dem Batch zum Opfer.
    mergeStaticMeshes(car.group);
    attachPaintSurfaces(car, null);
  }
  attachStencil(car);
  return car;
}

/**
 * Materialien pro Instanz klonen und Körperfarbe aus der Identität setzen.
 * instance() teilt Materialien — ohne Klon färbt ein Waggon alle anderen.
 */
function applyModelIdentity(car, root) {
  const bodyCol = identityBodyColor(car.identity);
  const cloned = new Map(); // original.uuid -> Klon (ein Klon pro Original pro Waggon)

  root.traverse((obj) => {
    if (!obj.isMesh || !obj.material) return;
    // paint_* bekommen gleich ein eigenes Overlay-Material.
    if (obj.name.startsWith('paint_')) return;

    const list = Array.isArray(obj.material) ? obj.material : [obj.material];
    const next = list.map((m) => {
      if (!m) return m;
      let c = cloned.get(m.uuid);
      if (!c) {
        c = m.clone();
        cloned.set(m.uuid, c);
        if (c.color && shouldRecolorMesh(obj.name)) {
          c.color.copy(bodyCol);
        }
      }
      return c;
    });
    obj.material = Array.isArray(obj.material) ? next : next[0];
  });
}

/** Körper-Meshes (nicht Stahl-Detail, Holz, Kohle) bekommen die Identitätsfarbe. */
function shouldRecolorMesh(name) {
  const n = (name || '').toLowerCase();
  if (!n) return true;
  if (n.includes('wood') || n.includes('coal') || n.includes('sleeper')) return false;
  if (n.includes('steeldark') || n.includes('steel_dark')) return false;
  // clay / body-steel / railRust = bemalte Außenhaut
  return true;
}

function identityBodyColor(identity) {
  _idColor.set(identity.baseColor);
  const rust = identity.rustLevel;
  _idColor.offsetHSL(0, -rust * 0.25, -rust * 0.18);
  _rustColor.set(PALETTE.railRust);
  _idColor.lerp(_rustColor, rust * 0.35);
  return _idColor.clone();
}

/** Schiebetür am Boxcar: part_door (und optional part_door_steelDark) in +Z-Richtung. */
function applyModelDoor(car, modelInst) {
  const doors = [];
  for (const [key, obj] of modelInst.parts) {
    if (key === 'door' || key.startsWith('door')) doors.push(obj);
  }
  if (!doors.length) return;
  car._doorParts = doors;
  // Geschlossen: Modell-Default. Offen: entlang lokaler Z um Türbreite schieben.
  if (car.doorOpen) {
    const doorW = car.type.interior?.doors?.[0]?.width ?? 2.4;
    for (const d of doors) {
      d.position.z -= doorW + 0.15;
    }
  }
}

/** Decks und Leiter-Zonen — Logik aus den Buildern, ohne Meshes (Modell-Pfad). */
function setupCarDecksAndGrabs(car) {
  const L = car.type.length;
  const W = car.type.width;
  const deckH = car.type.deckHeight;

  switch (car.typeId) {
    case 'loco': {
      const hoodLen = 11.2;
      const hoodH = 2.1;
      const hoodZ = L * 0.5 - 1.1 - hoodLen * 0.5;
      const porchDepth = 1.15;
      const porchY = deckH;
      deck(car, -W * 0.4, W * 0.4, L * 0.5 - porchDepth, L * 0.5 - 0.05, porchY + 0.06, 'porch');
      deck(car, -W * 0.4, W * 0.4, -L * 0.5 + 0.05, -L * 0.5 + porchDepth, porchY + 0.06, 'porch');
      deck(car, -0.28, 0.28, hoodZ - hoodLen * 0.4, hoodZ + hoodLen * 0.4, deckH + hoodH + 0.1, 'top');
      break;
    }
    case 'hopper': {
      const wallH = 2.15;
      const load = Math.max(0.2, Math.min(1, car.loadLevel));
      const coalY = deckH + 0.45 + load * (wallH - 0.7);
      const rimY = deckH + wallH;
      deck(car, -W * 0.38, W * 0.38, -L * 0.36, L * 0.36, coalY + 0.06, 'top');
      deck(car, -W * 0.5, -W * 0.38, -L * 0.4, L * 0.4, rimY + 0.05, 'top');
      deck(car, W * 0.38, W * 0.5, -L * 0.4, L * 0.4, rimY + 0.05, 'top');
      break;
    }
    case 'boxcar': {
      const interior = car.type.interior;
      const floorY = interior.floorY;
      deck(car, -interior.width * 0.5, interior.width * 0.5, -interior.length * 0.5, interior.length * 0.5, floorY, 'inside');
      break;
    }
    case 'tank': {
      const radius = 1.35;
      const tankLen = L * 0.78;
      const tankY = deckH + radius + 0.1;
      const porchD = 1.0;
      for (const sign of [1, -1]) {
        const z = sign * (L * 0.5 - porchD * 0.5);
        deck(car, -W * 0.38, W * 0.38, z - porchD * 0.45, z + porchD * 0.45, deckH + 0.05, 'porch');
      }
      deck(car, -0.25, 0.25, -tankLen * 0.35, tankLen * 0.35, tankY + radius + 0.45, 'top');
      break;
    }
    case 'gondola': {
      const interior = car.type.interior;
      const floorY = interior.floorY;
      deck(car, -interior.width * 0.5, interior.width * 0.5, -interior.length * 0.5, interior.length * 0.5, floorY, 'inside');
      break;
    }
    case 'flat':
    default: {
      deck(car, -W * 0.48, W * 0.48, -L * 0.47, L * 0.47, deckH + 0.08, 'top');
      break;
    }
  }
  // Leitern auch am Modell-Pfad zeichnen: Zone und Silhouette müssen
  // zusammenfallen (Konzept §11 — nur greifen, was man sieht). Modelle
  // haben oft eigene Leitern; die Code-Box markiert die greifbare Zone.
  installLadders(car, true);
}

/** Kohleladung für Hopper — unabhängig vom Karosserie-Modell. */
function addHopperCoal(car) {
  const L = car.type.length;
  const W = car.type.width;
  const deckH = car.type.deckHeight;
  const wallH = 2.15;
  const load = Math.max(0.2, Math.min(1, car.loadLevel));
  const coalY = deckH + 0.45 + load * (wallH - 0.7);
  const coalH = 0.4 + load * 0.25;
  const m = addMesh(car.group, box(W * 0.82, coalH, L * 0.78), mat('coal'), 0, coalY - coalH * 0.35, 0);
  m.userData.noMerge = true;
  addMesh(car.group, box(W * 0.5, 0.14, L * 0.4), mat('coalDust'), 0.1, coalY + 0.02, 0.2).userData.noMerge = true;
}

/**
 * Alle reinen Körper-Meshes mit gleichem Material zu einem Draw Call
 * zusammenfassen. Geteilte Geometrien aus style.js werden vorher geklont,
 * damit der Cache nicht zerstört wird.
 */
function mergeStaticMeshes(group) {
  const byMat = new Map();
  const victims = [];

  for (const child of group.children) {
    if (!child.isMesh) continue;
    if (child.userData.surfaceId) continue;
    if (child.userData.noMerge) continue;
    const key = child.material.uuid;
    if (!byMat.has(key)) byMat.set(key, { material: child.material, geos: [] });
    child.updateMatrix();
    const g = child.geometry.clone();
    g.applyMatrix4(child.matrix);
    byMat.get(key).geos.push(g);
    victims.push(child);
  }

  for (const v of victims) group.remove(v);

  for (const { material, geos } of byMat.values()) {
    if (geos.length === 0) continue;
    if (geos.length === 1) {
      const mesh = new THREE.Mesh(geos[0], material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
      continue;
    }
    const merged = mergeGeometries(geos, false);
    for (const g of geos) g.dispose();
    if (!merged) continue;
    const mesh = new THREE.Mesh(merged, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    group.add(mesh);
  }
}

function makeBodyMaterial(identity) {
  const key = `${identity.baseColor}|${identity.rustLevel.toFixed(2)}`;
  if (bodyMatCache.has(key)) return bodyMatCache.get(key);
  const c = new THREE.Color(identity.baseColor);
  // Rost zieht Sättigung und Helligkeit — Waggons altern unterschiedlich.
  const rust = identity.rustLevel;
  c.offsetHSL(0, -rust * 0.25, -rust * 0.18);
  c.lerp(new THREE.Color(PALETTE.railRust), rust * 0.35);
  const m = new THREE.MeshLambertMaterial({ color: c.getHex() });
  bodyMatCache.set(key, m);
  return m;
}

function addMesh(parent, geo, material, x, y, z, rx = 0, ry = 0, rz = 0) {
  const mesh = new THREE.Mesh(geo, material);
  mesh.position.set(x, y, z);
  if (rx || ry || rz) mesh.rotation.set(rx, ry, rz);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  parent.add(mesh);
  return mesh;
}

/**
 * Untergestell in drei Meshes: Rahmen + zwei Drehgestell-Blöcke.
 * Einzelräder würden allein ~88 Draw Calls kosten — Silhouette reicht.
 */
function addUnderframe(car, frameLen) {
  const steel = mat('steelDark');
  const w = car.type.width;
  addMesh(car.group, box(w * 0.88, 0.22, frameLen), steel, 0, 0.48, 0);
  const bogieZ = frameLen * 0.33;
  // Blockiges Drehgestell inkl. angedeuteter Radsätze
  addMesh(car.group, box(w * 0.78, 0.4, 1.7), steel, 0, 0.28, -bogieZ);
  addMesh(car.group, box(w * 0.78, 0.4, 1.7), steel, 0, 0.28, bogieZ);
}

/**
 * Leiter-Silhouette. Greifzone wird separat über installLadders gesetzt —
 * Mesh und Zone teilen dieselben (x,z,y0,y1), damit nichts auseinanderfällt.
 */
function addLadderMesh(car, x, z, y0, y1) {
  const h = Math.max(0.4, y1 - y0);
  addMesh(car.group, box(0.12, h, 0.32), matShiny('steel'), x, y0 + h * 0.5, z);
}

/**
 * Eine Quelle für Leiter-Zonen pro Waggontyp.
 * climbSides aus CAR_TYPES steuert, welche Seiten greifbar sind;
 * Positionen entsprechen den gezeichneten Leitern (bzw. ergänzen sie).
 *
 * ladder: { x, z, y0, y1, side, halfAlong, halfSide }
 *   - vertikale Linie / schlanke Zone, greifbar auf jeder Höhe y0..y1
 */
function ladderSpecsFor(car) {
  const L = car.type.length;
  const W = car.type.width;
  const deckH = car.type.deckHeight;
  const sides = new Set(car.type.climbSides ?? []);
  const out = [];

  const add = (x, z, y0, y1, side) => {
    // Nur Seiten aus climbSides — keine greifbaren glatten Flächen (Konzept §11).
    if (sides.size && !sides.has(side)) return;
    out.push({
      x, z, y0, y1, side,
      halfAlong: 0.28,
      halfSide: 0.22,
    });
  };

  switch (car.typeId) {
    case 'loco': {
      const porchY = deckH;
      const hoodLen = 11.2;
      const hoodZ = L * 0.5 - 1.1 - hoodLen * 0.5;
      // Nur Front-Leitern laut climbSides; Porch-Einstieg.
      add(-W * 0.48, L * 0.5 - 0.4, porchY, porchY + 1.55, 'front-left');
      add(W * 0.48, L * 0.5 - 0.4, porchY, porchY + 1.55, 'front-right');
      // Seitliche Hauben-Griffe nur wenn left/right in sides (loco hat sie nicht).
      void hoodZ;
      break;
    }
    case 'hopper': {
      const rimY = deckH + 2.15 + 0.1;
      // Vier Eckleitern — deckt left/right und wirkt auch von front/back erreichbar.
      add(-W * 0.52, L * 0.38, deckH, rimY, 'left');
      add(-W * 0.52, -L * 0.38, deckH, rimY, 'left');
      add(W * 0.52, L * 0.38, deckH, rimY, 'right');
      add(W * 0.52, -L * 0.38, deckH, rimY, 'right');
      // Stirnleitern für front/back (climbSides).
      add(-W * 0.35, L * 0.48, deckH, rimY, 'front');
      add(W * 0.35, -L * 0.48, deckH, rimY, 'back');
      break;
    }
    case 'boxcar': {
      const interior = car.type.interior;
      const floorY = interior.floorY;
      const wallH = interior.height + 0.15;
      const top = floorY + wallH * 0.7;
      // Endleitern links/rechts (sichtbar bisher bei z=+L*0.45).
      add(-W * 0.52, L * 0.45, deckH, top, 'left');
      add(W * 0.52, L * 0.45, deckH, top, 'right');
      add(-W * 0.52, -L * 0.45, deckH, top, 'left');
      add(W * 0.52, -L * 0.45, deckH, top, 'right');
      break;
    }
    case 'tank': {
      const radius = 1.35;
      const tankY = deckH + radius + 0.1;
      const top = tankY + radius + 0.3;
      // climbSides: front/back — Leitern an den Enden.
      add(W * 0.5, L * 0.38, deckH, top, 'front');
      add(-W * 0.5, -L * 0.38, deckH, top, 'back');
      // Seitliche Endbühnen-Griffe als kurze Zonen (Porch).
      add(-W * 0.4, L * 0.5 - 0.5, deckH, deckH + 1.35, 'front');
      add(W * 0.4, L * 0.5 - 0.5, deckH, deckH + 1.35, 'front');
      add(-W * 0.4, -(L * 0.5 - 0.5), deckH, deckH + 1.35, 'back');
      add(W * 0.4, -(L * 0.5 - 0.5), deckH, deckH + 1.35, 'back');
      break;
    }
    case 'gondola': {
      const interior = car.type.interior;
      const floorY = interior.floorY;
      const wallH = interior.height;
      const top = floorY + wallH;
      add(-W * 0.52, L * 0.4, deckH, top, 'left');
      add(-W * 0.52, -L * 0.4, deckH, top, 'left');
      add(W * 0.52, L * 0.4, deckH, top, 'right');
      add(W * 0.52, -L * 0.4, deckH, top, 'right');
      add(-W * 0.35, L * 0.48, deckH, top, 'front');
      add(W * 0.35, -L * 0.48, deckH, top, 'back');
      break;
    }
    case 'flat':
    default: {
      // Flachwagen: kurze Rungen/Griffe an den Ecken (sichtbare Holme).
      const top = deckH + 0.75;
      add(-W * 0.5, L * 0.42, deckH - 0.15, top, 'left');
      add(-W * 0.5, -L * 0.42, deckH - 0.15, top, 'left');
      add(W * 0.5, L * 0.42, deckH - 0.15, top, 'right');
      add(W * 0.5, -L * 0.42, deckH - 0.15, top, 'right');
      add(0, L * 0.48, deckH - 0.15, top, 'front');
      add(0, -L * 0.48, deckH - 0.15, top, 'back');
      break;
    }
  }
  return out;
}

/**
 * Schreibt car.ladders + car.grabs (Mittelpunkts-Samples für check-train).
 * drawMeshes: bei Code-Geometrie Leitern zeichnen.
 */
function installLadders(car, drawMeshes) {
  const specs = ladderSpecsFor(car);
  car.ladders.length = 0;
  car.grabs.length = 0;
  // grabs = Höhen-Samples aus Leitern (Kompat: check-train verlangt grabs[]).
  for (const s of specs) {
    car.ladders.push(s);
    const midY = (s.y0 + s.y1) * 0.5;
    car.grabs.push({ x: s.x, y: s.y0 + 0.35, z: s.z, side: s.side });
    car.grabs.push({ x: s.x, y: midY, z: s.z, side: s.side });
    if (drawMeshes) addLadderMesh(car, s.x, s.z, s.y0, s.y1);
  }
}

function grab(car, x, y, z, side) {
  car.grabs.push({ x, y, z, side });
}

function deck(car, minX, maxX, minZ, maxZ, y, kind) {
  car.decks.push({ minX, maxX, minZ, maxZ, y, kind });
}

// --- Typen ------------------------------------------------------------------

function buildLoco(car, body) {
  const L = car.type.length;
  const W = car.type.width;
  const deckH = car.type.deckHeight;
  addUnderframe(car, L * 0.92);

  // Motorhaube (lang, niedrig) + Führerhaus (hoch) — Silhouette muss „Kopf" lesen.
  const hoodLen = 11.2;
  const hoodH = 2.1;
  const cabLen = 4.2;
  const cabH = 3.15;
  const hoodZ = L * 0.5 - 1.1 - hoodLen * 0.5;
  const cabZ = hoodZ - hoodLen * 0.5 - cabLen * 0.5 - 0.1;

  addMesh(car.group, box(W * 0.88, hoodH, hoodLen), body, 0, deckH + hoodH * 0.5, hoodZ);
  addMesh(car.group, box(W * 0.95, cabH, cabLen), body, 0, deckH + cabH * 0.5, cabZ);

  // Dach + kurze Nase vorne
  addMesh(car.group, box(W * 0.98, 0.16, cabLen + 0.2), mat('steelDark'), 0, deckH + cabH + 0.08, cabZ);
  addMesh(car.group, box(W * 0.7, 1.2, 1.4), body, 0, deckH + 0.7, L * 0.5 - 0.9);

  // Ein Fensterband vorne — Seitenfenster sind aus der Ferne unsichtbar.
  addMesh(car.group, box(W * 0.7, 0.7, 0.08), mat('steelDark'), 0, deckH + 2.2, cabZ - cabLen * 0.5);

  // Endbühnen inkl. angedeutetem Geländer (eine Box pro Ende)
  const porchY = deckH;
  const porchDepth = 1.15;
  addMesh(car.group, box(W * 0.9, 0.12, porchDepth), mat('steel'), 0, porchY, L * 0.5 - porchDepth * 0.5);
  addMesh(car.group, box(W * 0.9, 0.12, porchDepth), mat('steel'), 0, porchY, -L * 0.5 + porchDepth * 0.5);
  addMesh(car.group, box(W * 0.9, 1.0, 0.1), matShiny('steel'), 0, porchY + 0.55, L * 0.5 - 0.15);
  addMesh(car.group, box(W * 0.9, 1.0, 0.1), matShiny('steel'), 0, porchY + 0.55, -L * 0.5 + 0.15);

  // Laufsteg auf der Haube
  addMesh(car.group, box(0.55, 0.08, hoodLen * 0.9), mat('steel'), 0, deckH + hoodH + 0.06, hoodZ);

  deck(car, -W * 0.4, W * 0.4, L * 0.5 - porchDepth, L * 0.5 - 0.05, porchY + 0.06, 'porch');
  deck(car, -W * 0.4, W * 0.4, -L * 0.5 + 0.05, -L * 0.5 + porchDepth, porchY + 0.06, 'porch');
  deck(car, -0.28, 0.28, hoodZ - hoodLen * 0.4, hoodZ + hoodLen * 0.4, deckH + hoodH + 0.1, 'top');
  installLadders(car, true);
}

function buildHopper(car, body) {
  const L = car.type.length;
  const W = car.type.width;
  const deckH = car.type.deckHeight;
  const wallH = 2.15;
  addUnderframe(car, L * 0.94);

  // Äußere Längswände
  addMesh(car.group, box(0.12, wallH, L * 0.88), body, -W * 0.48, deckH + wallH * 0.5, 0);
  addMesh(car.group, box(0.12, wallH, L * 0.88), body, W * 0.48, deckH + wallH * 0.5, 0);
  // Stirnwände
  addMesh(car.group, box(W * 0.96, wallH, 0.12), body, 0, deckH + wallH * 0.5, L * 0.44);
  addMesh(car.group, box(W * 0.96, wallH, 0.12), body, 0, deckH + wallH * 0.5, -L * 0.44);

  // Trichter: zwei geneigte Platten (Doppelbunker-Silhouette)
  const slope = mat('steelDark');
  addMesh(car.group, box(W * 0.85, 0.16, L * 0.4), slope, 0, deckH + 0.4, L * 0.15, 0.5, 0, 0);
  addMesh(car.group, box(W * 0.85, 0.16, L * 0.4), slope, 0, deckH + 0.4, -L * 0.15, -0.5, 0, 0);

  // Kohleladung — begehbar; eine zweite Platte bricht die Fläche
  const load = Math.max(0.2, Math.min(1, car.loadLevel));
  const coalY = deckH + 0.45 + load * (wallH - 0.7);
  const coalH = 0.4 + load * 0.25;
  addMesh(car.group, box(W * 0.82, coalH, L * 0.78), mat('coal'), 0, coalY - coalH * 0.35, 0);
  addMesh(car.group, box(W * 0.5, 0.14, L * 0.4), mat('coalDust'), 0.1, coalY + 0.02, 0.2);

  // Randbleche (schmal begehbar)
  const rimY = deckH + wallH;
  addMesh(car.group, box(0.22, 0.1, L * 0.86), mat('steel'), -W * 0.45, rimY, 0);
  addMesh(car.group, box(0.22, 0.1, L * 0.86), mat('steel'), W * 0.45, rimY, 0);

  deck(car, -W * 0.38, W * 0.38, -L * 0.36, L * 0.36, coalY + 0.06, 'top');
  deck(car, -W * 0.5, -W * 0.38, -L * 0.4, L * 0.4, rimY + 0.05, 'top');
  deck(car, W * 0.38, W * 0.5, -L * 0.4, L * 0.4, rimY + 0.05, 'top');
  installLadders(car, true);
}

function buildBoxcar(car, body) {
  const L = car.type.length;
  const W = car.type.width;
  const deckH = car.type.deckHeight;
  const interior = car.type.interior;
  const wallH = interior.height + 0.15;
  const floorY = interior.floorY;
  addUnderframe(car, L * 0.94);

  // Boden (außen und innen begehbar)
  addMesh(car.group, box(W * 0.96, 0.14, L * 0.94), mat('woodDark'), 0, floorY - 0.07, 0);

  // Dach
  addMesh(car.group, box(W * 1.02, 0.18, L * 0.96), mat('steelDark'), 0, floorY + wallH + 0.05, 0);

  // Stirnwände
  addMesh(car.group, box(W * 0.96, wallH, 0.12), body, 0, floorY + wallH * 0.5, L * 0.47);
  addMesh(car.group, box(W * 0.96, wallH, 0.12), body, 0, floorY + wallH * 0.5, -L * 0.47);

  // Seitenwände als Segmente um die Tür — sonst kein betretbares Loch.
  const doorW = interior.doors[0]?.width ?? 2.4;
  const halfDoor = doorW * 0.5;
  const sideZ = L * 0.45;
  const panelThick = 0.1;
  const wallX = W * 0.5 - panelThick * 0.5;

  for (const side of [-1, 1]) {
    const x = side * wallX;
    if (car.doorOpen) {
      const segLen = sideZ - halfDoor;
      addMesh(car.group, box(panelThick, wallH, segLen), body, x, floorY + wallH * 0.5, halfDoor + segLen * 0.5);
      addMesh(car.group, box(panelThick, wallH, segLen), body, x, floorY + wallH * 0.5, -halfDoor - segLen * 0.5);
      // Geschobene Tür
      addMesh(
        car.group,
        box(panelThick * 0.8, wallH * 0.92, doorW),
        mat('steelDark'),
        x + side * 0.08,
        floorY + wallH * 0.46,
        -(halfDoor + 1.1),
      );
    } else {
      addMesh(car.group, box(panelThick, wallH, L * 0.9), body, x, floorY + wallH * 0.5, 0);
    }
  }

  deck(car, -interior.width * 0.5, interior.width * 0.5, -interior.length * 0.5, interior.length * 0.5, floorY, 'inside');
  installLadders(car, true);
}

function buildTank(car, body) {
  const L = car.type.length;
  const W = car.type.width;
  const deckH = car.type.deckHeight;
  addUnderframe(car, L * 0.94);

  // Liegender Kessel — glänzendes Metall mit Waggonfarbe
  const radius = 1.35;
  const tankLen = L * 0.78;
  const tankY = deckH + radius + 0.1;
  const tankCol = body.color.clone().multiplyScalar(0.9);
  const tKey = `tank|${tankCol.getHexString()}`;
  let tMat = bodyMatCache.get(tKey);
  if (!tMat) {
    tMat = new THREE.MeshPhongMaterial({
      color: tankCol.getHex(),
      shininess: 28,
      specular: 0x333333,
    });
    bodyMatCache.set(tKey, tMat);
  }
  addMesh(car.group, cylinder(radius, radius, tankLen, 12), tMat, 0, tankY, 0, 0, 0, Math.PI / 2);

  // Sattel + Dom
  addMesh(car.group, box(W * 0.7, 0.4, 2.4), mat('steelDark'), 0, deckH + 0.35, 0);
  addMesh(car.group, cylinder(0.35, 0.35, 0.5, 8), tMat, 0, tankY + radius + 0.15, 0);

  // Laufsteg (Geländer in einer Box mit dem Steg verschmolzen über Höhe)
  addMesh(car.group, box(0.55, 0.12, tankLen * 0.85), mat('steel'), 0, tankY + radius + 0.38, 0);

  // Endbühnen
  const porchD = 1.0;
  for (const sign of [1, -1]) {
    const z = sign * (L * 0.5 - porchD * 0.5);
    addMesh(car.group, box(W * 0.85, 0.1, porchD), mat('steel'), 0, deckH, z);
    addMesh(car.group, box(W * 0.85, 0.9, 0.1), matShiny('steel'), 0, deckH + 0.5, z + sign * 0.4);
    deck(car, -W * 0.38, W * 0.38, z - porchD * 0.45, z + porchD * 0.45, deckH + 0.05, 'porch');
  }

  deck(car, -0.25, 0.25, -tankLen * 0.35, tankLen * 0.35, tankY + radius + 0.45, 'top');
  installLadders(car, true);
}

function buildGondola(car, body) {
  const L = car.type.length;
  const W = car.type.width;
  const deckH = car.type.deckHeight;
  const interior = car.type.interior;
  const wallH = interior.height; // ~1.9 m
  const floorY = interior.floorY;
  addUnderframe(car, L * 0.94);

  addMesh(car.group, box(W * 0.96, 0.14, L * 0.92), mat('steelDark'), 0, floorY - 0.07, 0);

  // Wände oben offen
  const t = 0.1;
  addMesh(car.group, box(t, wallH, L * 0.9), body, -W * 0.48, floorY + wallH * 0.5, 0);
  addMesh(car.group, box(t, wallH, L * 0.9), body, W * 0.48, floorY + wallH * 0.5, 0);
  addMesh(car.group, box(W * 0.96, wallH, t), body, 0, floorY + wallH * 0.5, L * 0.45);
  addMesh(car.group, box(W * 0.96, wallH, t), body, 0, floorY + wallH * 0.5, -L * 0.45);

  deck(car, -interior.width * 0.5, interior.width * 0.5, -interior.length * 0.5, interior.length * 0.5, floorY, 'inside');
  installLadders(car, true);
}

function buildFlat(car, body) {
  const L = car.type.length;
  const W = car.type.width;
  const deckH = car.type.deckHeight;
  addUnderframe(car, L * 0.94);

  // Deckplatte — der ehrliche nasse Platz; Holme als Kante am Deck
  addMesh(car.group, box(W, 0.2, L * 0.96), mat('woodOld'), 0, deckH, 0);
  addMesh(car.group, box(0.14, 0.35, L * 0.96), body, -W * 0.5 + 0.07, deckH + 0.08, 0);
  addMesh(car.group, box(0.14, 0.35, L * 0.96), body, W * 0.5 - 0.07, deckH + 0.08, 0);
  // Zwei Rungen statt vier — Silhouette bleibt lesbar
  addMesh(car.group, box(0.14, 0.55, 0.14), mat('steel'), -W * 0.45, deckH + 0.35, L * 0.4);
  addMesh(car.group, box(0.14, 0.55, 0.14), mat('steel'), W * 0.45, deckH + 0.35, -L * 0.4);

  deck(car, -W * 0.48, W * 0.48, -L * 0.47, L * 0.47, deckH + 0.08, 'top');
  installLadders(car, true);
}

// --- Malflächen & Schablonen ------------------------------------------------

/**
 * Hängt Malflächen an. Mit Modell: paint_* übernehmen die Rolle der
 * Overlay-Planes (userData.isPaint muss true sein — sonst macht canvas.js
 * die Fläche zur opaken Wand und Graffiti verschwindet / Wände werden
 * durchsichtig). Ohne passendes paint_*: Code-Plane wie bisher.
 */
function attachPaintSurfaces(car, modelInst) {
  const W = car.type.width;
  const L = car.type.length;

  for (const surface of car.paintSurfaces) {
    // Canvas zuerst: leere transparente Textur, bis jemand malt.
    const canvas = makeCanvas(surface.pxW, surface.pxH);
    let map = null;
    if (canvas && typeof canvas.getContext === 'function') {
      const ctx2d = canvas.getContext('2d');
      if (ctx2d) {
        ctx2d.clearRect(0, 0, canvas.width, canvas.height);
        map = new THREE.CanvasTexture(canvas);
        map.colorSpace = THREE.SRGBColorSpace;
        map.needsUpdate = true;
      }
    }

    // Overlay-Material: transparent über dem Körper. isPaint steuert
    // SurfaceManager.#isPaintOverlay — ohne das wird der Canvas weiß gefüllt
    // und die Fläche deckt die Wand zu statt nur Graffiti zu zeigen.
    const material = new THREE.MeshLambertMaterial({
      color: 0xffffff,
      map,
      transparent: true,
      alphaTest: 0.02,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    material.userData.isPaint = true;

    // Modell-Malfläche: short-id aus surfaceId ('car-02:l' → 'l') → paint_l.
    const shortId = surface.surfaceId.includes(':')
      ? surface.surfaceId.slice(surface.surfaceId.lastIndexOf(':') + 1)
      : surface.side;
    const modelPaint = modelInst?.paintSurfaces?.get(shortId) ?? null;
    let mesh;
    if (modelPaint && modelPaint.isMesh) {
      mesh = modelPaint;
      // Eigenes Material pro Waggon+Fläche — nie das geteilte glTF-Material.
      mesh.material = material;
      mesh.userData.surfaceId = surface.surfaceId;
      mesh.userData.isPaint = true;
      mesh.receiveShadow = true;
      // Kein erneutes group.add — sitzt schon im Modell.
    } else {
      const geo = plane(surface.widthM, surface.heightM);
      mesh = new THREE.Mesh(geo, material);
      mesh.userData.surfaceId = surface.surfaceId;
      mesh.userData.isPaint = true;
      mesh.receiveShadow = true;

      const cy = surface.centerY;
      const inset = 0.07;

      if (surface.location === 'interior') {
        // Innenflächen hängen an den Wänden und zeigen nach innen.
        if (surface.side === 'inner-left') {
          mesh.position.set(-W * 0.5 + 0.16, cy, 0);
          mesh.rotation.y = Math.PI / 2;
        } else if (surface.side === 'inner-right') {
          mesh.position.set(W * 0.5 - 0.16, cy, 0);
          mesh.rotation.y = -Math.PI / 2;
        } else {
          mesh.position.set(0, cy, 0);
        }
      } else {
        switch (surface.side) {
          case 'left':
            mesh.position.set(-W * 0.5 - inset, cy, 0);
            mesh.rotation.y = -Math.PI / 2;
            break;
          case 'right':
            mesh.position.set(W * 0.5 + inset, cy, 0);
            mesh.rotation.y = Math.PI / 2;
            break;
          case 'back':
            mesh.position.set(0, cy, -L * 0.5 - inset);
            mesh.rotation.y = Math.PI;
            break;
          case 'front':
            mesh.position.set(0, cy, L * 0.5 + inset);
            break;
          default:
            mesh.position.set(0, cy, 0);
        }
      }

      car.group.add(mesh);
    }

    surface.canvas = canvas;
    surface.mesh = mesh;
    surface.material = material;
  }
}

function attachStencil(car) {
  // Einzige erlaubte Schrift in der Welt — Reporting Mark + Nummer.
  const text = car.identity.stencil;
  const tex = makeStencilTexture(text, car.id);
  if (!tex) return;

  const key = `stencil|${car.id}`;
  let material = stencilMatCache.get(key);
  if (!material) {
    material = new THREE.MeshLambertMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
      color: 0xffffff,
    });
    stencilMatCache.set(key, material);
  }

  const W = car.type.width;
  const y = Math.max(1.2, car.type.deckHeight + 1.0);
  const plateW = Math.min(3.2, car.type.length * 0.28);
  const plateH = 0.45;

  // Beide Seiten: Wiedererkennung von beiden Gleisseiten.
  for (const side of [-1, 1]) {
    const mesh = new THREE.Mesh(plane(plateW, plateH), material);
    mesh.position.set(side * (W * 0.5 + 0.09), y, car.typeId === 'loco' ? 2.0 : 0);
    mesh.rotation.y = side < 0 ? -Math.PI / 2 : Math.PI / 2;
    car.group.add(mesh);
  }
}

/** Höchstens ein Schadens-Detail pro Waggon — Wiedererkennung ohne Mesh-Explosion. */
function applyDamageDetails(car) {
  const damage = car.identity.damage || [];
  if (!damage.length) return;
  const tag = damage[0];
  const W = car.type.width;
  const L = car.type.length;
  const y = car.type.deckHeight + 1.4;
  switch (tag) {
    case 'dent-left':
      addMesh(car.group, box(0.12, 0.7, 1.1), mat('steelDark'), -W * 0.5 - 0.02, y, L * 0.12);
      break;
    case 'dent-right':
      addMesh(car.group, box(0.12, 0.7, 1.1), mat('steelDark'), W * 0.5 + 0.02, y, -L * 0.1);
      break;
    case 'rust-streak':
    case 'burn-mark':
      addMesh(car.group, box(0.3, 1.3, 0.12), mat('railRust'), -W * 0.48, y, L * 0.15);
      break;
    case 'patched-panel':
    case 'missing-plate':
      addMesh(car.group, box(0.08, 0.9, 1.2), mat('corrugated'), W * 0.5 + 0.03, y, 0.4);
      break;
    case 'oil-stain':
      addMesh(car.group, box(0.9, 0.08, 1.2), mat('coalDust'), 0.15, car.type.deckHeight + 0.02, -0.2);
      break;
    default:
      break;
  }
}

// --- Canvas-Hilfen (Browser + Node-Test) ------------------------------------

function makeCanvas(w, h) {
  if (typeof document !== 'undefined' && document.createElement) {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    return c;
  }
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(w, h);
  }
  // Minimal-Stub für Node: PaintSystem bekommt Maße, keine Pixel.
  return {
    width: w,
    height: h,
    getContext: () => null,
  };
}

function makeStencilTexture(text, carId) {
  const w = 256;
  const h = 64;
  const canvas = makeCanvas(w, h);
  const ctx2d = canvas.getContext?.('2d');
  if (!ctx2d) {
    // Node ohne Canvas: einfarbige DataTexture als Platzhalter.
    const data = new Uint8Array([240, 240, 235, 180]);
    const tex = new THREE.DataTexture(data, 1, 1);
    tex.needsUpdate = true;
    return tex;
  }

  ctx2d.clearRect(0, 0, w, h);
  // Leicht abgenutzte Schablonen-Schrift
  ctx2d.fillStyle = 'rgba(0,0,0,0)';
  ctx2d.fillRect(0, 0, w, h);
  ctx2d.font = 'bold 28px monospace';
  ctx2d.textAlign = 'center';
  ctx2d.textBaseline = 'middle';
  ctx2d.fillStyle = 'rgba(245,245,240,0.92)';
  ctx2d.fillText(text, w * 0.5, h * 0.52);

  // Abnutzung: ein paar transparente Löcher
  let seed = 0;
  for (let i = 0; i < carId.length; i++) seed = (seed * 33 + carId.charCodeAt(i)) | 0;
  ctx2d.globalCompositeOperation = 'destination-out';
  for (let i = 0; i < 18; i++) {
    seed = (seed * 1103515245 + 12345) | 0;
    const x = ((seed >>> 16) & 255) / 255 * w;
    seed = (seed * 1103515245 + 12345) | 0;
    const y = ((seed >>> 16) & 255) / 255 * h;
    ctx2d.fillRect(x, y, 3 + (seed & 3), 2);
  }
  ctx2d.globalCompositeOperation = 'source-over';

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}
